/**
 * @fileoverview Better Auth React Client wrapper
 * 
 * @purpose Provides client-side hooks and utilities for Better Auth.
 *          Replaces next-auth/react with Better Auth implementation.
 * 
 * @usage Import in client components:
 *        import { useSession, signIn, signOut, authClient } from '@korvo/core/lib/better-auth/client'
 * 
 * @ownership Core infrastructure - authentication client
 * 
 * @dependencies
 * - better-auth/react (Better Auth React integration)
 * 
 * @restrictions
 * - DO NOT use in server components - client-only
 * - Requires Better Auth to be configured in the app
 * 
 * @docs
 * - See derivative app templates for Better Auth setup
 */

"use client";

import { createAuthClient } from "better-auth/react";

import { betterAuthClientFetchOnError } from "./better-auth-fetch-on-error";

// Get base URL for client
// Client-side: use window.location.origin
// Server-side (SSR): use relative URLs (empty string = same origin)
function getBaseUrl(): string {
  if (typeof window !== "undefined") {
    // Client-side: use current origin
    return window.location.origin;
  }
  
  // Server-side (SSR): Use relative URLs (empty string = same origin)
  // Better Auth will make requests relative to the current request origin
  // This avoids requiring Node.js scripts in client components
  return '';
}

/**
 * Better Auth client instance for React components
 * 
 * Usage:
 * ```tsx
 * import { useSession, signIn, signOut } from '@korvo/core/lib/better-auth/client';
 * 
 * const { data: session, isPending } = useSession();
 * await signIn.email({ email, password });
 * await signOut();
 * ```
 * 
 * Note: baseURL is determined at runtime:
 * - Client-side: uses window.location.origin
 * - Server-side (SSR): uses NEXT_PUBLIC_APP_URL or relative URLs
 */
export const authClient = createAuthClient({
  baseURL: getBaseUrl(),
  // Add fetch configuration to handle errors gracefully
  fetchOptions: {
    onError: betterAuthClientFetchOnError,
  },
});

// Explicit re-exports so bundlers can resolve them (destructuring breaks static analysis)
export const useSession = authClient.useSession;
export const signIn = authClient.signIn;
export const signUp = authClient.signUp;
export const signOut = authClient.signOut;
// Optional hook-style exports if the library provides them
export const useSignIn = (authClient as Record<string, unknown>).useSignIn as typeof authClient.signIn | undefined;
export const useSignUp = (authClient as Record<string, unknown>).useSignUp as typeof authClient.signUp | undefined;
export const useSignOut = (authClient as Record<string, unknown>).useSignOut as typeof authClient.signOut | undefined;

// Export types
export type Session = typeof authClient.$Infer.Session;
export type User = Session['user'];
